'use strict';
import { constantStrings } from '../constants/constantStrings';

/**
 * Helper class for formatting study data for display
 */
export class StudyDisplayHelpers {
  static formatLocation(study, locations) {
    let matchingLocation = locations.find(loc => loc.Key === study.Location);

    if (matchingLocation && matchingLocation.Value) {
      if (study.Type === constantStrings.StudyTypes.Outside && study.Location === '0') {
        return matchingLocation.Value.trim();
      } else {
        return matchingLocation
          ? matchingLocation.Value.split(',').slice(1).join(' ').trim()
          : '';
      }
    }
    return '';
  }

  static formatDStatus(study, dStatusTypes) {
    if (study.Type === constantStrings.StudyTypes.Regular) {
      if (study.DStatus) {
        const dStatus = dStatusTypes.find(ds => ds.code === study.DStatus);
        return dStatus ? dStatus.label : '';
      }
      return '';
    } else if (study.Type === constantStrings.StudyTypes.Outside) {
      return 'Outside Exam';
    }
    return '';
  }

  static formatAssessment(study, biRads) {
    let assessment = '';
    let isShowViewReport = false;

    if (study.Type === constantStrings.StudyTypes.Regular) {
      if (study.RecallFor) {
        const birad = biRads.find(b => b.code === study.RecallFor);
        if (birad && !isNaN(Number(birad.label))) {
          isShowViewReport = true;
          assessment = `BI-RADS ${birad.label}`;
        }
      }
    } else if (study.Type === constantStrings.StudyTypes.Outside) {
      assessment = study.Result;
    }
    return { assessment, isShowViewReport };
  }

  static formatRegularStudyDescription(study, uniqueLinkedTo) {
    let description = '';

    if (!study.Procedures || !study.Procedures.length) {
      return description;
    }

    if (study.LinkedTo) {
      description += '(Linked) ';
    } else if (uniqueLinkedTo.length > 0 && uniqueLinkedTo.includes(study.SDate)) {
      description += '(Primary) ';
    }

    study.Procedures.forEach((procedure, index) => {
      const side = procedure.Side === 'L' ? 'Left' :
                  procedure.Side === 'R' ? 'Right' :
                  procedure.Side === 'B' ? 'Bilateral' : '';
      
      const name = procedure.Name;

      if (name && side) {
        description += `${name} - ${side}`;
        if (index < study.Procedures.length - 1) {
          description += ', ';
        }
      }
    });
    return description;
  }

  static formatOutsideStudyDescription(study, procTypes, viewsTypes) {
    const proctype = procTypes.find(pt => pt.Key === study.Proctype);

    const vType = study.VType ? study.ScrPro : study.ScrPro;
    const scrpro = viewsTypes.find(vr => vr.Key === vType);

    const proctypeValue = proctype ? proctype.Value : '';
    const scrproValue = scrpro ? scrpro.Value : '';

    if (proctypeValue && scrproValue) {
      return proctypeValue + ' - ' + scrproValue;
    } else if (proctypeValue) {
      return proctypeValue;
    } else if (scrproValue) {
      return scrproValue;
    }
    return '';
  }

  static formatDescription(study, params) {
    const { uniqueLinkedTo, procTypes, viewsTypes, visitTypes, visitReasons } = params;
    
    let description = '';

    if (study.Type === constantStrings.StudyTypes.Regular) {
      description = this.formatRegularStudyDescription(study, uniqueLinkedTo);
      
      const matchingVisitType = visitTypes.find(visitType => visitType.code === study.VType);
      if (matchingVisitType && matchingVisitType.label) {
        description += ' - ' + matchingVisitType.label;
      }
    } else if (study.Type === constantStrings.StudyTypes.Outside) {
      description = this.formatOutsideStudyDescription(study, procTypes, viewsTypes);
    }

    const matchingVisitType = visitTypes.find(visitType => visitType.code === study.VType);
    const matchingVisitReason = visitReasons.find(vr => vr.Key === study.VType);

    const visitLabel = matchingVisitType
      ? matchingVisitType.label
      : matchingVisitReason
        ? matchingVisitReason.Value
        : 'Other';

    if (description) {
      description += ' - ' + visitLabel;
    } else {
      description += visitLabel;
    }
    return description;
  }

  static formatTechnologist(study) {
    return study.TechFName && study.TechLName 
      ? `${study.TechLName}, ${study.TechFName}` 
      : '';
  }

  static formatVType(study, visitTypes, visitReasons) {
    const matchingVisitType = visitTypes.find(visitType => visitType.code === study.VType);
    const matchingVisitReason = visitReasons.find(vr => vr.Key === study.VType);
    return matchingVisitType
      ? matchingVisitType.label
      : matchingVisitReason
        ? matchingVisitReason.Value
        : 'Other';
  }

  static transformStudyForDisplay(study, params) {
    const { locations, visitTypes, visitReasons, dStatusTypes, biRads, procTypes, viewsTypes, uniqueLinkedTo } = params;

    const locationToDisplay = this.formatLocation(study, locations);
    const dStatusToDisplay = this.formatDStatus(study, dStatusTypes);
    const { assessment, isShowViewReport } = this.formatAssessment(study, biRads);
    const descriptionToDisplay = this.formatDescription(study, { 
      uniqueLinkedTo, 
      procTypes, 
      viewsTypes, 
      visitTypes, 
      visitReasons 
    });
    const technologistToDisplay = this.formatTechnologist(study);
    const vTypeToDisplay = this.formatVType(study, visitTypes, visitReasons);

    return {
      ...study,
      TechnologistToDisplay: technologistToDisplay,
      LocationToDisplay: locationToDisplay,
      VTypeToDisplay: vTypeToDisplay,
      AssessmentToDisplay: assessment,
      DStatusToDisplay: dStatusToDisplay,
      DescriptionToDisplay: descriptionToDisplay,
      IsShowViewReport: isShowViewReport,
    };
  }

  static transformStudiesForDisplay(studies, params) {
    if (!studies || !studies.length) {
      return [];
    }

    const uniqueLinkedTo = [...new Set(studies.map(item => item.LinkedTo))];
    return studies.map(study => 
      this.transformStudyForDisplay(study, { ...params, uniqueLinkedTo })
    );
  }
}




// WEBPACK FOOTER //
// ./src/scripts/helpers/studyDisplayHelpers.js